import React, {
	createContext,
	useContext,
	useState,
	useEffect,
	ReactNode,
	useRef,
	useCallback
} from 'react'
import useWebSocket, { ReadyState } from 'react-use-websocket'

// Define types
type RefetchFunction = () => Promise<any> | void

interface WebSocketContextValue {
	sendMessage: (message: string | ArrayBufferLike | Blob | ArrayBufferView) => void
	lastMessage: MessageEvent<any> | null
	readyState: ReadyState
	messageHistory: MessageEvent<any>[]
	registerRefetch: (refetchFn: RefetchFunction) => () => void
	closeSocket: () => void
}

interface WebSocketProviderProps {
	children: ReactNode
	socketUrl: string
}

// Create context with default values
const WebSocketContext = createContext<WebSocketContextValue | undefined>(undefined)

export function WebSocketProvider({
	children,
	socketUrl
}: WebSocketProviderProps): JSX.Element {
	const refetchFunctionsRef = useRef<RefetchFunction[]>([])
	const [messageHistory, setMessageHistory] = useState<MessageEvent<any>[]>([])
	const didUnmount = useRef(false)

	const { sendMessage, lastMessage, readyState, getWebSocket } = useWebSocket(socketUrl, {
		reconnectAttempts: 10,
		reconnectInterval: 5000,
		shouldReconnect: closeEvent => {
			return true
		}
	})
	const [wsUrl, setWsUrl] = useState(socketUrl)

	const sendPing = useCallback(() => {
		if (readyState === ReadyState.OPEN) {
			sendMessage('ping')
		}
	}, [readyState, sendMessage])

	// Set up interval for sending ping messages
	useEffect(() => {
		const pingInterval = setInterval(sendPing, 1000)

		return () => {
			clearInterval(pingInterval)
		}
	}, [sendPing])

	// Handle incoming messages
	useEffect(() => {
		if (readyState === ReadyState.OPEN) {
			sendMessage('Hello, WebSocket!')
		}

		if (lastMessage) {
			console.log('Message received:', lastMessage)
			setMessageHistory(prev => prev.concat(lastMessage))

			// Trigger all registered refetch functions from the ref
			refetchFunctionsRef.current.forEach(refetch => refetch())
		}
	}, [lastMessage, readyState]) // Remove refetchFunctions from dependencies

	// Register a refetch function using the ref
	const registerRefetch = (refetchFn: RefetchFunction): (() => void) => {
		refetchFunctionsRef.current.push(refetchFn)

		return () => {
			refetchFunctionsRef.current = refetchFunctionsRef.current.filter(
				fn => fn !== refetchFn
			)
		}
	}
	const closeSocket = () => {
		getWebSocket()?.close()
	}
	const contextValue: WebSocketContextValue = {
		sendMessage,
		lastMessage,
		readyState,
		messageHistory,
		registerRefetch,
		closeSocket
	}

	return (
		<WebSocketContext.Provider value={contextValue}>{children}</WebSocketContext.Provider>
	)
}

export function useGlobalWebSocket(): WebSocketContextValue {
	const context = useContext(WebSocketContext)
	if (context === undefined) {
		throw new Error('useGlobalWebSocket must be used within a WebSocketProvider')
	}
	return context
}
